// src/pages/SuperDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Divider,
  Tooltip,
  IconButton,
  Button,
  Stack,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import SecurityIcon from "@mui/icons-material/Security";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyIcon from "@mui/icons-material/VpnKey";

import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  orderBy,
  limit as qLimit,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../authContext";


/** --------------------------- helpers --------------------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const lastNDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};
const clampPct = (n) => (isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0);
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n ?? "0");
const human = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 2 : 0)} ${u[i]}`;
};


// Which collections to include when estimating Firestore usage per company.
// NOTE: We filter by companyId except where noted.
const USAGE_DEFS = [
  { name: "tasks",                 where: (cid) => [["companyId","==",cid]] },
  { name: "activityLogs",          where: (cid) => [["companyId","==",cid]] },
  { name: "timesheets",            where: (cid) => [["companyId","==",cid]] }, // stores minutes/hours, dateKey, project info
  { name: "users",                 where: (cid) => [["companyId","==",cid]] },
  { name: "projects",              where: (cid) => [["companyId","==",cid]] },
  { name: "entities",              where: (cid) => [["companyId","==",cid]] },
  { name: "projectMemberships",    where: (cid) => [["companyId","==",cid]] }, // ensured by CFN on project/task writes
  { name: "managerMemberLinks",    where: (cid) => [["companyId","==",cid]] }, // ensured by CFN linking manager↔member
  { name: "licenses",              where: (cid) => [["assignedCompanyId","==",cid]] }, // only licenses assigned to this company
  // intentionally skipped: contact_requests (no companyId), companyUsage (output of this process)
];

// Build a query for a given collection def
const buildQueryFor = (db, def, companyId) => {
  let qRef = collection(db, def.name);
  const clauses = (def.where && def.where(companyId)) || [];
  for (const [f, op, v] of clauses) qRef = query(qRef, where(f, op, v));
  return qRef;
};

// Estimate average serialized doc size from a small sample
const estimateAvgSizeBytes = async (qRef) => {
  const snap = await getDocs(query(qRef, qLimit(200)));
  if (snap.empty) return 0;
  let total = 0;
  snap.forEach((d) => {
    // very close to wire size for most JSON-y docs
    total += JSON.stringify(d.data()).length;
    // small overhead for ids/metadata
    total += 80;
  });
  return Math.round(total / snap.size);
};

// Pretty size
const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B","KB","MB","GB","TB"];
  const e = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, e)).toFixed(e ? 2 : 0)} ${u[e]}`;
};


/** Small KPI tile */
function StatTile({ icon, title, value, subtitle, tint = "#fff", iconBg = "#EEF2FF" }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: 3,
        bgcolor: tint,
        border: "1px solid #E6E8F0",
        display: "flex",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          bgcolor: iconBg,
          display: "grid",
          placeItems: "center",
          color: "#1F2937",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" sx={{ lineHeight: 1.15 }}>
          {fmt(value)}
        </Typography>
        {!!subtitle && <Typography variant="caption">{subtitle}</Typography>}
      </Box>
    </Paper>
  );
}

/** Progress bar row (for % metrics) */
function BarRow({ label, value, color = "primary" }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2">{clampPct(value)}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={clampPct(value)} color={color} />
    </Box>
  );
}

/** --------------------------- License Issuer & Manager (Owner-only) --------------------------- */
function LicenseIssuerManager({ onRefresh }) {
  const db = getFirestore();
  const { platformRole } = useAuth();
  const isOwner = (platformRole || "").toLowerCase() === "owner";

  const [licenses, setLicenses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [msg, setMsg] = React.useState({ type: "", text: "" });

  // editing state
  const empty = {
    id: "",
    licenseKey: "",
    status: "active",
    validFrom: "",
    validTo: "",
    assignedCompanyId: "",
    assignedCompanyName: "",
  };
  const [form, setForm] = React.useState(empty);
  const [saving, setSaving] = React.useState(false);

  const toISO = (ts) =>
    (ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null)?.toISOString?.().slice(0, 10) || "";

  const load = React.useCallback(async () => {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "licenses"), orderBy("createdAt", "desc"), qLimit(100)));
    const rows = snap.docs.map((d) => {
      const v = d.data();
      return {
        id: d.id,
        ...v,
        validFromISO: toISO(v.validFrom),
        validToISO: toISO(v.validTo),
      };
    });
    setLicenses(rows);
    setLoading(false);
  }, [db]);

  React.useEffect(() => {
    load();
  }, [load]);

  const genKey = () => {
    const seg = () => Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    setForm((f) => ({ ...f, licenseKey: `WP-${seg()}-${seg()}-${seg()}` }));
  };

  const onPick = (lic) => {
    setForm({
      id: lic.id,
      licenseKey: lic.licenseKey || "",
      status: lic.status || "active",
      validFrom: lic.validFromISO || "",
      validTo: lic.validToISO || "",
      assignedCompanyId: lic.assignedCompanyId || "",
      assignedCompanyName: lic.assignedCompanyName || "",
    });
    setMsg({ type: "", text: "" });
  };

  const clearForm = () => {
    setForm(empty);
    setMsg({ type: "", text: "" });
  };

  const createNew = async () => {
    if (!isOwner) return;
    if (!form.licenseKey) {
      setMsg({ type: "error", text: "License Key required." });
      return;
    }

    setSaving(true);
    try {
      // uniqueness check
      const exists = await getDocs(query(collection(db, "licenses"), where("licenseKey", "==", form.licenseKey)));
      if (!exists.empty) throw new Error("This license key already exists.");

      await addDoc(collection(db, "licenses"), {
        licenseKey: form.licenseKey,
        status: form.status || "active",
        validFrom: form.validFrom ? Timestamp.fromDate(new Date(`${form.validFrom}T00:00:00`)) : null,
        validTo: form.validTo ? Timestamp.fromDate(new Date(`${form.validTo}T23:59:59`)) : null,
        assignedCompanyId: null,
        assignedCompanyName: null,
        assignedAt: null,
        createdAt: serverTimestamp(),
      });
      clearForm();
      await load();
      setMsg({ type: "success", text: "License created." });
      onRefresh?.();
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Failed to create license." });
    } finally {
      setSaving(false);
    }
  };

  const saveExisting = async () => {
    if (!isOwner) return;
    if (!form.id) return;
    setSaving(true);
    try {
      const vf = form.validFrom ? Timestamp.fromDate(new Date(`${form.validFrom}T00:00:00`)) : null;
      const vt = form.validTo ? Timestamp.fromDate(new Date(`${form.validTo}T23:59:59`)) : null;

      await updateDoc(doc(db, "licenses", form.id), {
        licenseKey: form.licenseKey,
        status: form.status,
        validFrom: vf,
        validTo: vt,
      });

      if (form.assignedCompanyId) {
        await updateDoc(doc(db, "companies", form.assignedCompanyId), {
          licenseStatus: form.status,
          licenseValidFrom: vf,
          licenseValidTo: vt,
          licenseUpdatedAt: serverTimestamp(),
        });
      }

      await load();
      setMsg({ type: "success", text: "License saved." });
      onRefresh?.();
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Failed to save license." });
    } finally {
      setSaving(false);
    }
  };

  const unassign = async () => {
    if (!isOwner || !form.id) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "licenses", form.id), {
        assignedCompanyId: null,
        assignedCompanyName: null,
        assignedAt: null,
      });

      if (form.assignedCompanyId) {
        await updateDoc(doc(db, "companies", form.assignedCompanyId), {
          licenseKey: null,
          licenseStatus: null,
          licenseValidFrom: null,
          licenseValidTo: null,
          licenseAssignedAt: null,
          licenseUpdatedAt: serverTimestamp(),
        });
      }

      await load();
      setMsg({ type: "success", text: "License unassigned." });
      setForm((f) => ({ ...f, assignedCompanyId: "", assignedCompanyName: "" }));
      onRefresh?.();
    } catch (e) {
      setMsg({ type: "error", text: e?.message || "Failed to unassign." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          <KeyIcon sx={{ mr: 1 }} /> License Issuer & Manager
        </Typography>
        <Tooltip title="Owner-only">
          <SecurityIcon color={isOwner ? "success" : "disabled"} />
        </Tooltip>
      </Box>
      <Divider sx={{ my: 1.25 }} />

      {loading ? (
        <LinearProgress />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="License Key"
              value={form.licenseKey}
              onChange={(e) => setForm({ ...form, licenseKey: e.target.value })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Valid From"
                  value={form.validFrom}
                  onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Valid To"
                  value={form.validTo}
                  onChange={(e) => setForm({ ...form, validTo: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </TextField>

            <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={genKey}>
                Generate
              </Button>
              {!form.id ? (
                <Button variant="contained" onClick={createNew} disabled={saving || !isOwner}>
                  Create License
                </Button>
              ) : (
                <>
                  <Button variant="contained" onClick={saveExisting} disabled={saving || !isOwner}>
                    Save
                  </Button>
                  {!!form.assignedCompanyId && (
                    <Button color="warning" onClick={unassign} disabled={saving || !isOwner}>
                      Unassign
                    </Button>
                  )}
                  <Button onClick={clearForm} disabled={saving}>
                    Clear
                  </Button>
                </>
              )}
            </Stack>

            {msg.text ? (
              <Alert sx={{ mt: 2 }} severity={msg.type === "error" ? "error" : "success"}>
                {msg.text}
              </Alert>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Recent Licenses
            </Typography>
            <Box sx={{ maxHeight: 330, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8 }}>Key</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Valid</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Assigned To</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((l) => (
                    <tr
                      key={l.id}
                      style={{ borderTop: "1px solid #EEE", cursor: "pointer" }}
                      onClick={() => onPick(l)}
                    >
                      <td style={{ padding: 8 }}>{l.licenseKey}</td>
                      <td style={{ padding: 8 }}>
                        <Chip size="small" color={l.status === "active" ? "success" : "warning"} label={l.status} />
                      </td>
                      <td style={{ padding: 8 }}>
                        {l.validFromISO || "—"} → {l.validToISO || "—"}
                      </td>
                      <td style={{ padding: 8 }}>{l.assignedCompanyName || l.assignedCompanyId || "Unassigned"}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Click a row to edit. Generating before sharing the key keeps control with you.
            </Typography>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

/** --------------------------- main component --------------------------- */
export default function SuperDashboard() {
  const db = getFirestore();
  const { platformRole } = useAuth();
  const isOwner = (platformRole || "").toLowerCase() === "owner";

  const [loading, setLoading] = useState(true);
  const [refresher, setRefresher] = useState(0);

  // usage panel state (selected company details)
  const [companyList, setCompanyList] = useState([]); // [{id: companyId, name}]
  const [selectedCompany, setSelectedCompany] = useState("");
  const [usage, setUsage] = useState(null);
  const [usageBusy, setUsageBusy] = useState(false);
  const [usageMsg, setUsageMsg] = useState("");

  // NEW: concise usage table across companies
  const [usageTable, setUsageTable] = useState([]); // [{companyId,name,docs,fsBytes,stBytes,total,updatedAt}]
  const [usageTableBusy, setUsageTableBusy] = useState(false);

  // NEW: contact requests
  const [contacts, setContacts] = useState([]); // recent contact_requests
  const [contactsBusy, setContactsBusy] = useState(false);
  const CONTACT_STATUSES = ["new", "in_review", "contacted", "closed"];

  /** top-level tallies (keep existing KPIs) */
  const [counts, setCounts] = useState({
    companies: 0,
    users: 0,
    projects: 0,
    tasks: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
  });

  /** engagement & retention */
  const [activeUsers, setActiveUsers] = useState({ dau: 0, wau: 0, mau: 0 });
  const [activeCompanies, setActiveCompanies] = useState({ d7: 0, d30: 0, growth30: 0 });
  const [stickiness, setStickiness] = useState(0);
  const [churn, setChurn] = useState(0);

  /** adoption / company level */
  const [adoption, setAdoption] = useState([]);
  const [dropOff, setDropOff] = useState([]);

  /** expansion vs contraction */
  const [expansion, setExpansion] = useState({ up: 0, down: 0, movers: [] });

  /** security / auth */
  const [loginPulse, setLoginPulse] = useState({ last24h: 0, last7d: 0 });

  /** recent signups for context */
  const [recentCompanies, setRecentCompanies] = useState([]);

  // ---- usage helpers ----
  const functions = getFunctions(undefined, "us-central1");
  const runUsage = httpsCallable(functions, "runCompanyUsageOnce");

  const readLatestUsage = async (companyId) => {
    const snap = await getDoc(doc(db, "companyUsage", companyId, "daily", "latest"));
    return snap.exists() ? snap.data() : null;
  };

  const refreshUsageSelected = async () => {
    if (!selectedCompany) {
      setUsageMsg("Please select a company.");
      return;
    }
    setUsageBusy(true);
    setUsageMsg("");
    try {
      await runUsage({ companyId: selectedCompany });
      const data = await readLatestUsage(selectedCompany);
      setUsage(data);
      if (!data) setUsageMsg("No usage record found.");
    } catch (e) {
      setUsageMsg(e?.message || "Failed to refresh usage.");
    } finally {
      setUsageBusy(false);
    }
  };

const [backfillBusy, setBackfillBusy] = useState(false);

  // Build rows for the collection summary (within the selected company)
  const usageRows = useMemo(() => {
   // baseline rows for all collections we care about
   const baseline = Object.fromEntries(
     USAGE_DEFS.map(d => [d.name, { count: 0, approxBytes: 0 }])
   );
   const collections = { ...baseline, ...(usage?.collections || {}) };
   // prefer server total; if missing, compute from the map
   const total = usage?.totals?.firestoreBytes ??
     Object.values(collections).reduce((s, v) => s + (v?.approxBytes || 0), 0);
   return Object.entries(collections)
     .map(([name, v]) => ({
       name,
       count: v?.count || 0,
       bytes: v?.approxBytes || 0,
       share: total ? (100 * (v?.approxBytes || 0)) / total : 0,
     }))
     .sort((a, b) => b.bytes - a.bytes);
 }, [usage]);

  // NEW: load concise usage table (all companies)
  const loadUsageTable = async (companies) => {
    setUsageTableBusy(true);
    try {
      const rows = await Promise.all(
        companies.map(async (c) => {
          const d = await readLatestUsage(c.companyId);
          return {
            companyId: c.companyId,
            name: c.companyName || c.companyId,
            docs: d?.totals?.firestoreDocs ?? 0,
            fsBytes: d?.totals?.firestoreBytes ?? 0,
            stBytes: d?.totals?.storageBytes ?? 0,
            total: (d?.totals?.combinedBytes ?? ((d?.totals?.firestoreBytes || 0) + (d?.totals?.storageBytes || 0))),
            updatedAt: d?.at?.toDate ? d.at.toDate() : d?.at ? new Date(d.at) : null,
            hasUsage: !!d,
          };
        })
      );
      setUsageTable(rows.sort((a, b) => (b.total || 0) - (a.total || 0)));
    } finally {
      setUsageTableBusy(false);
    }
  };

  // NEW: recalc usage for one company row (inline)
  const recalcRow = async (companyId) => {
    try {
      await runUsage({ companyId });
      const d = await readLatestUsage(companyId);
      setUsageTable((old) =>
        old.map((r) =>
          r.companyId === companyId
            ? {
                ...r,
                docs: d?.totals?.firestoreDocs ?? 0,
                fsBytes: d?.totals?.firestoreBytes ?? 0,
                stBytes: d?.totals?.storageBytes ?? 0,
                total:
                  d?.totals?.combinedBytes ??
                  ((d?.totals?.firestoreBytes || 0) + (d?.totals?.storageBytes || 0)),
                updatedAt: d?.at?.toDate ? d.at.toDate() : d?.at ? new Date(d.at) : null,
                hasUsage: !!d,
              }
            : r
        )
      );
    } catch (e) {
      // swallow; UI hint not necessary
    }
  };


const backfillSelected = async () => {
  if (!selectedCompany) {
    setUsageMsg("Please select a company first.");
    return;
  }
  // Optional confirm
  if (!window.confirm(`Backfill links for ${selectedCompany}?`)) return;

  setBackfillBusy(true);
  setUsageMsg("");
  try {
    const fns = getFunctions(undefined, "us-central1");
    const run = httpsCallable(fns, "backfillCompanyMemberships");
    const res = await run({ companyId: selectedCompany });
    const { fixedPM = 0, fixedMML = 0 } = (res?.data || {});
    setUsageMsg(`Backfill complete. projectMemberships fixed: ${fixedPM}, managerMemberLinks fixed: ${fixedMML}.`);
    // Now re-read the latest server usage (or run it again if you want)
    await refreshUsageSelected();
  } catch (e) {
    setUsageMsg(e?.message || "Backfill failed.");
  } finally {
    setBackfillBusy(false);
  }
};


// Recalculate usage for ALL companies (sequentially to avoid hammering Functions)
const recalcAll = async () => {
  setUsageTableBusy(true);
  try {
    for (const r of usageTable) {
      // sequential on purpose; keep it gentle on the backend
      // eslint-disable-next-line no-await-in-loop
      await recalcRow(r.companyId);
    }
  } finally {
    setUsageTableBusy(false);
  }
};

  // NEW: recalc all (sequential)
  // Replace your existing recalc function with this:
const recalcUsageForCompany = async (companyId) => {
  setUsageBusy(true);
  setUsageMsg("Scanning collections…");

  try {
    const rows = [];

    // scan each configured collection
    for (const def of USAGE_DEFS) {
      const qRef = buildQueryFor(db, def, companyId);

      // fast count
      const agg = await getCountFromServer(qRef);
      const count = agg.data().count || 0;

      // estimate avg size from a small sample
      const avg = await estimateAvgSizeBytes(qRef);
      const estimatedBytes = count * avg;

      rows.push({
        name: def.name,
        count,
        avgBytes: avg,
        estimatedBytes,
      });
    }

    // sort by estimated size desc for readability
    rows.sort((a, b) => b.estimatedBytes - a.estimatedBytes);

    const firestoreTotal = rows.reduce((s, r) => s + r.estimatedBytes, 0);
    const storageTotal = 0; // keep if you later add Storage paths
    const combined = firestoreTotal + storageTotal;

    // Reshape to match what the UI reads
    const collections = {};
    for (const r of rows) {
      collections[r.name] = { count: r.count, approxBytes: r.estimatedBytes };
    }
    setUsage({
      at: new Date().toISOString(),
      method: { sampledPerCollection: 200, source: "client-estimate" },
      totals: {
        firestoreBytes: firestoreTotal,
        storageBytes: storageTotal,
        combinedBytes: combined,
        firestoreDocs: rows.reduce((s, r) => s + r.count, 0),
        firestoreReadable: fmtBytes(firestoreTotal),
        storageReadable: fmtBytes(storageTotal),
        combinedReadable: fmtBytes(combined),
      },
      collections,
    });

    setUsageMsg("Done");
  } catch (e) {
    console.error(e);
    setUsageMsg(e.message || "Failed to recalculate.");
  } finally {
    setUsageBusy(false);
    // triggers your “last updated …” timestamp in the UI
    setRefresher((n) => n + 1);
  }
};


  // NEW: load contact requests
  const loadContacts = async () => {
    setContactsBusy(true);
    try {
      const snap = await getDocs(
        query(collection(db, "contact_requests"), orderBy("createdAt", "desc"), qLimit(50))
      );
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setContacts(rows);
    } finally {
      setContactsBusy(false);
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await updateDoc(doc(db, "contact_requests", id), {
        status,
        updatedAt: serverTimestamp(),
      });
      setContacts((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      // no-op
    }
  };

  useEffect(() => {
    if (!isOwner) return;

    (async () => {
      setLoading(true);

      /** ---------- global fast counters ---------- */
      const [cAgg, uAgg, pAgg, tAgg] = await Promise.all([
        getCountFromServer(collection(db, "companies")),
        getCountFromServer(collection(db, "users")),
        getCountFromServer(collection(db, "projects")),
        getCountFromServer(collection(db, "tasks")),
      ]);

      const statusCount = (s) =>
        getCountFromServer(query(collection(db, "tasks"), where("status", "==", s))).then(
          (a) => a.data().count || 0
        );

      const [pending, inProgress, completed] = await Promise.all([
        statusCount("Pending"),
        statusCount("In Progress"),
        statusCount("Completed"),
      ]);

      setCounts({
        companies: cAgg.data().count || 0,
        users: uAgg.data().count || 0,
        projects: pAgg.data().count || 0,
        tasks: tAgg.data().count || 0,
        pending,
        inProgress,
        completed,
      });

      /** ---------- companies snapshot ---------- */
      const csnap = await getDocs(collection(db, "companies"));
      const companies = csnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCompanyList(companies.map((c) => ({ id: c.companyId, name: c.companyName || c.companyId })));
      if (!selectedCompany && companies.length) setSelectedCompany(companies[0].companyId);

      // load concise usage table across companies
      await loadUsageTable(companies);

      // load contact form submissions
      await loadContacts();

      /** ---------- engagement (DAU / WAU / MAU) ---------- */
      const [d1, d7, d30] = [1, 7, 30].map(lastNDaysISO);
      const [dauAgg, wauAgg, mauAgg] = await Promise.all([
        getCountFromServer(query(collection(db, "users"), where("lastLoginAt", ">=", d1))),
        getCountFromServer(query(collection(db, "users"), where("lastLoginAt", ">=", d7))),
        getCountFromServer(query(collection(db, "users"), where("lastLoginAt", ">=", d30))),
      ]);
      const dau = dauAgg.data().count || 0;
      const wau = wauAgg.data().count || 0;
      const mau = mauAgg.data().count || 0;
      setActiveUsers({ dau, wau, mau });
      setStickiness(mau ? (dau / mau) * 100 : 0);

      /** active companies & churn + adoption per company */
      const thirtyAgo = lastNDaysISO(30);
      const prevThirtyStart = lastNDaysISO(60);
      const prevThirtyEnd = lastNDaysISO(31);

      const perCompanyPromises = companies.map(async (c) => {
        // users
        const [totalUsersAgg, active30Agg, activePrev30Agg] = await Promise.all([
          getCountFromServer(query(collection(db, "users"), where("companyId", "==", c.companyId))),
          getCountFromServer(
            query(collection(db, "users"), where("companyId", "==", c.companyId), where("lastLoginAt", ">=", thirtyAgo))
          ),
          getCountFromServer(
            query(
              collection(db, "users"),
              where("companyId", "==", c.companyId),
              where("lastLoginAt", ">=", prevThirtyStart),
              where("lastLoginAt", "<", prevThirtyEnd)
            )
          ),
        ]);

        const usersTotal = totalUsersAgg.data().count || 0;
        const usersActive30 = active30Agg.data().count || 0;
        const usersPrev30 = activePrev30Agg.data().count || 0;

        // projects / tasks
        const [projAgg, tasksAgg, completedAgg] = await Promise.all([
          getCountFromServer(query(collection(db, "projects"), where("companyId", "==", c.companyId))),
          getCountFromServer(query(collection(db, "tasks"), where("companyId", "==", c.companyId))),
          getCountFromServer(
            query(collection(db, "tasks"), where("companyId", "==", c.companyId), where("status", "==", "Completed"))
          ),
        ]);

        const projects = projAgg.data().count || 0;
        const tasks = tasksAgg.data().count || 0;
        const completedTasks = completedAgg.data().count || 0;

        // open & overdue for risk
        const [openAgg, overdueAgg] = await Promise.all([
          getCountFromServer(
            query(
              collection(db, "tasks"),
              where("companyId", "==", c.companyId),
              where("status", "in", ["Pending", "In Progress"])
            )
          ),
          getCountFromServer(query(collection(db, "tasks"), where("companyId", "==", c.companyId), where("isOverdue", "==", true))),
        ]);

        const open = openAgg.data().count || 0;
        const overdue = overdueAgg.data().count || 0;

        return {
          companyId: c.companyId,
          name: c.companyName || c.companyId,
          users: usersTotal,
          active30: usersActive30,
          prevActive30: usersPrev30,
          projects,
          tasks,
          completed: completedTasks,
          open,
          overdue,
          pctActive: usersTotal ? (usersActive30 / usersTotal) * 100 : 0,
          approxDocs: usersTotal + projects + tasks,
        };
      });

      const perCompany = await Promise.all(perCompanyPromises);

      // active companies & churn
      const active7 = await Promise.all(
        companies.map(async (c) => {
          const agg = await getCountFromServer(
            query(collection(db, "users"), where("companyId", "==", c.companyId), where("lastLoginAt", ">=", lastNDaysISO(7)))
          );
          return (agg.data().count || 0) > 0 ? 1 : 0;
        })
      );
      const active30 = perCompany.filter((r) => r.active30 > 0).length;
      const churnPct = companies.length > 0 ? ((companies.length - active30) / companies.length) * 100 : 0;

      // expansion vs contraction
      const movers = perCompany
        .map((r) => ({ ...r, delta: r.active30 - r.prevActive30 }))
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
        .slice(0, 10);
      const up = perCompany.filter((r) => r.delta > 0).length;
      const down = perCompany.filter((r) => r.delta < 0).length;

      // drop-off
      const drop = perCompany.filter((r) => r.projects > 0 && r.tasks === 0);

      // login pulse
      const [last24Agg, last7Agg] = await Promise.all([
        getCountFromServer(query(collection(db, "users"), where("lastLoginAt", ">=", lastNDaysISO(1)))),
        getCountFromServer(query(collection(db, "users"), where("lastLoginAt", ">=", lastNDaysISO(7)))),
      ]);

      // recent signups (last 30 days)
      const recent = companies
        .filter((c) => c.createdAt && toDate(c.createdAt)?.toISOString() >= thirtyAgo)
        .sort((a, b) => toDate(b.createdAt) - toDate(a.createdAt))
        .slice(0, 12);

      // set state
      setActiveCompanies({
        d7: active7.reduce((a, b) => a + b, 0),
        d30: active30,
        growth30: companies.length > 0 ? (active30 / companies.length) * 100 : 0,
      });
      setChurn(churnPct);
      setAdoption(perCompany.sort((a, b) => b.tasks - a.tasks || b.projects - a.projects).slice(0, 25));
      setDropOff(drop.slice(0, 25));
      setExpansion({ up, down, movers });
      setLoginPulse({ last24h: last24Agg.data().count || 0, last7d: last7Agg.data().count || 0 });
      setRecentCompanies(recent);
      setLoading(false);
    })();
  }, [db, isOwner, refresher, selectedCompany]);

  // Auto-load latest usage when a company is selected (for detail panel)
  useEffect(() => {
    if (!selectedCompany) return;
    (async () => {
      const data = await readLatestUsage(selectedCompany);
      setUsage(data);
    })();
  }, [selectedCompany]);

  /** -------------- guards -------------- */
  if (!isOwner) {
    return (
      <Box p={3}>
        <Typography variant="h6">
          You need <b>Owner</b> access to view this page.
        </Typography>
      </Box>
    );
  }

  /** -------------- UI -------------- */
  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4">WorkPulse — Provider Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Quick usage & lead summary first; deeper analytics below.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={() => setRefresher((n) => n + 1)}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

{/* =================== SECTION 2: CONTACT REQUESTS (from /contact_requests) =================== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Contact Requests</Typography>
              <Button size="small" variant="outlined" onClick={loadContacts} disabled={contactsBusy}>
                {contactsBusy ? "Refreshing…" : "Refresh"}
              </Button>
            </Box>
            <Divider sx={{ my: 1.25 }} />
            {contactsBusy && <LinearProgress />}
            {contacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No requests yet.
              </Typography>
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8 }}>Name</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Email</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Company</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Size</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Country</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Discussion</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Status</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Created</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Update</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} style={{ borderTop: "1px solid #EEE", verticalAlign: "top" }}>
                      <td style={{ padding: 8 }}>
                        {(c.firstName || "") + " " + (c.lastName || "")}
                      </td>
                      <td style={{ padding: 8 }}>{c.companyEmail || "—"}</td>
                      <td style={{ padding: 8 }}>{c.companyName || "—"}</td>
                      <td style={{ padding: 8 }}>{c.companySize || "—"}</td>
                      <td style={{ padding: 8 }}>{c.country || "—"}</td>
                      <td style={{ padding: 8, maxWidth: 360 }}>
                        <Typography variant="body2" color="text.secondary" noWrap title={c.discussion}>
                          {c.discussion || "—"}
                        </Typography>
                      </td>
                      <td style={{ padding: 8 }}>
                        <Chip
                          size="small"
                          color={
                            c.status === "new"
                              ? "info"
                              : c.status === "in_review"
                              ? "warning"
                              : c.status === "contacted"
                              ? "success"
                              : "default"
                          }
                          label={c.status || "—"}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        {c.createdAt?.toDate ? c.createdAt.toDate().toLocaleString() : "—"}
                      </td>
                      <td style={{ padding: 8 }}>
                        <TextField
                          select
                          size="small"
                          value={c.status || "new"}
                          onChange={(e) => updateContactStatus(c.id, e.target.value)}
                          sx={{ minWidth: 140 }}
                        >
                          {CONTACT_STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* =================== SECTION 1: COMPANY USAGE SUMMARY (concise, all companies) =================== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Company Usage — Summary (Firestore + Storage)</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" disabled={usageTableBusy} onClick={recalcAll}>
                  {usageTableBusy ? "Updating…" : "Recalculate All"}
                </Button>
              </Stack>
            </Box>
            <Divider sx={{ my: 1.25 }} />
            {usageTableBusy && <LinearProgress />}
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 8 }}>Company</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Docs</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Firestore</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Storage</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Total</th>
                  <th style={{ textAlign: "left", padding: 8 }}>Updated</th>
                  <th style={{ textAlign: "left", padding: 8 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {usageTable.map((r) => (
                  <tr key={r.companyId} style={{ borderTop: "1px solid #EEE" }}>
                    <td style={{ padding: 8 }}>{r.name}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.docs)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{human(r.fsBytes)}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{human(r.stBytes)}</td>
                    <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>{human(r.total)}</td>
                    <td style={{ padding: 8 }}>
                      {r.updatedAt ? r.updatedAt.toLocaleString() : <Chip size="small" label="no run yet" />}
                    </td>
                    <td style={{ padding: 8 }}>
                      <Button size="small" onClick={() => recalcRow(r.companyId)}>
                        Recalculate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Paper>
        </Grid>
      </Grid>

    {/* =================== SECTION 3: DETAILED USAGE (pick company, recalc, breakdown) =================== */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Usage per Company (Detailed)</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  select
                  size="small"
                  label="Select Company"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  sx={{ minWidth: 280 }}
                >
                  {companyList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" onClick={refreshUsageSelected} disabled={usageBusy || !selectedCompany}>
                  {usageBusy ? "Calculating..." : "Recalculate Usage"}
                </Button>

                <Button
                variant="outlined"
                onClick={backfillSelected}
                disabled={backfillBusy || usageBusy || !selectedCompany}
              >
                {backfillBusy ? "Backfilling…" : "Backfill Links"}
              </Button>

              </Stack>
            </Box>
            <Divider sx={{ my: 1.25 }} />

            {!selectedCompany ? (
              <Typography variant="body2" color="text.secondary">
                Pick a company to see usage.
              </Typography>
            ) : usageBusy ? (
              <LinearProgress />
            ) : usage ? (
              <>
                {/* KPI tiles */}
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <StatTile
                      icon={<QueryStatsIcon />}
                      title="Firestore Data"
                      value={usage?.totals?.firestoreReadable || human(usage?.totals?.firestoreBytes || 0)}
                      subtitle={`${fmt(usage?.totals?.firestoreDocs || 0)} docs`}
                      iconBg="#E8F0FE"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatTile
                      icon={<SecurityIcon />}
                      title="Storage"
                      value={usage?.totals?.storageReadable || human(usage?.totals?.storageBytes || 0)}
                      subtitle={`${fmt(usage?.totals?.storageBytes || 0)} bytes`}
                      iconBg="#FFF3E0"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StatTile
                      icon={<QueryStatsIcon />}
                      title="Total"
                      value={usage?.totals?.combinedReadable || human(usage?.totals?.combinedBytes || 0)}
                      subtitle="Approximate"
                      iconBg="#E8F5E9"
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary">
                  Last updated:{" "}
                  {usage?.at?.toDate
                    ? usage.at.toDate().toLocaleString()
                    : usage?.at
                    ? new Date(usage.at).toLocaleString()
                    : "—"}
                  . Sampling method: {usage?.method?.sampledPerCollection ?? usage?.method?.samplingPerCollection ?? 0} docs /
                  collection.
                </Typography>

                {/* Collection-wise summary table */}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Collection breakdown
                </Typography>

                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: 8 }}>Collection</th>
                      <th style={{ textAlign: "right", padding: 8 }}>Documents</th>
                      <th style={{ textAlign: "right", padding: 8 }}>Estimated Size</th>
                      <th style={{ textAlign: "right", padding: 8 }}>Share of Firestore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map((r) => (
                      <tr key={r.name} style={{ borderTop: "1px solid #EEE" }}>
                        <td style={{ padding: 8 }}>{r.name}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.count)}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>{human(r.bytes)}</td>
                        <td style={{ padding: 8, textAlign: "right" }}>
                          <Chip size="small" label={`${clampPct(r.share)}%`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #DDD" }}>
                      <td style={{ padding: 8, fontWeight: 600 }}>Firestore Total</td>
                      <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>
                        {fmt(usage?.totals?.firestoreDocs || 0)}
                      </td>
                      <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>
                        {usage?.totals?.firestoreReadable || human(usage?.totals?.firestoreBytes || 0)}
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td style={{ padding: 8, fontWeight: 600 }}>Storage Total</td>
                      <td style={{ padding: 8, textAlign: "right" }}>—</td>
                      <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>
                        {usage?.totals?.storageReadable || human(usage?.totals?.storageBytes || 0)}
                      </td>
                      <td />
                    </tr>
                    <tr>
                      <td style={{ padding: 8, fontWeight: 700 }}>Combined</td>
                      <td style={{ padding: 8, textAlign: "right" }}>—</td>
                      <td style={{ padding: 8, textAlign: "right", fontWeight: 700 }}>
                        {usage?.totals?.combinedReadable || human(usage?.totals?.combinedBytes || 0)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No usage record yet. Click <b>Recalculate Usage</b>.
              </Typography>
            )}

            {usageMsg && (
              <Alert sx={{ mt: 1.5 }} severity="warning">
                {usageMsg}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* =================== SECTION 4+: original analytics (KPIs etc.) =================== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<BusinessIcon />} title="Companies" value={counts.companies} iconBg="#E8F0FE" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<PeopleIcon />} title="Users" value={counts.users} iconBg="#E8F5E9" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<WorkOutlineIcon />} title="Projects" value={counts.projects} iconBg="#FFF3E0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile icon={<ListAltIcon />} title="Tasks" value={counts.tasks} iconBg="#F3E8FF" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">Active Users</Typography>
            <Divider sx={{ my: 1.25 }} />
            <Stack spacing={1}>
              <BarRow label={`DAU (${fmt(activeUsers.dau)})`} value={stickiness} color="primary" />
              <Typography variant="body2" color="text.secondary">
                WAU: <b>{fmt(activeUsers.wau)}</b> • MAU: <b>{fmt(activeUsers.mau)}</b>
              </Typography>
            </Stack>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="caption" color="text.secondary">
              Stickiness ≈ DAU / MAU
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">Task Status (All Companies)</Typography>
            <Divider sx={{ my: 1.25 }} />
            <BarRow
              label={`Pending (${fmt(counts.pending)})`}
              value={(counts.pending / Math.max(1, counts.tasks)) * 100}
              color="warning"
            />
            <BarRow
              label={`In Progress (${fmt(counts.inProgress)})`}
              value={(counts.inProgress / Math.max(1, counts.tasks)) * 100}
              color="info"
            />
            <BarRow
              label={`Completed (${fmt(counts.completed)})`}
              value={(counts.completed / Math.max(1, counts.tasks)) * 100}
              color="success"
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">Active Companies</Typography>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="body2">
              Active in 7 days: <b>{fmt(activeCompanies.d7)}</b>
            </Typography>
            <Typography variant="body2">
              Active in 30 days: <b>{fmt(activeCompanies.d30)}</b>
            </Typography>
            <BarRow label="30-day activity coverage" value={activeCompanies.growth30} color="primary" />
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="body2">
              Churn (no login in 30d): <b>{clampPct(churn)}%</b>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Adoption per company */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Adoption by Company (top 25)</Typography>
              <Typography variant="body2" color="text.secondary">
                % Active = users with 30-day login / total users
              </Typography>
            </Box>
            <Divider sx={{ my: 1.25 }} />
            {adoption.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data yet.
              </Typography>
            ) : (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: 8 }}>Company</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Users</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Active 30d</th>
                    <th style={{ textAlign: "right", padding: 8 }}>% Active</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Projects</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Tasks</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Completed</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Open</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Overdue</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Docs≈</th>
                  </tr>
                </thead>
                <tbody>
                  {adoption.map((r) => (
                    <tr key={r.companyId} style={{ borderTop: "1px solid #EEE" }}>
                      <td style={{ padding: 8 }}>{r.name}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.users)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.active30)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{clampPct(r.pctActive)}%</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.projects)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.tasks)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.completed)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.open)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        <Chip size="small" color={r.overdue ? "error" : "default"} label={fmt(r.overdue)} />
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmt(r.approxDocs)}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Expansion vs Contraction + Drop-off */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6">Expansion vs Contraction</Typography>
            <Divider sx={{ my: 1.25 }} />
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Chip icon={<TrendingUpIcon />} color="success" label={`Up: ${fmt(expansion.up)}`} />
              <Chip icon={<TrendingDownIcon />} color="warning" label={`Down: ${fmt(expansion.down)}`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Top movers (Δ active users 30d vs prev 30d)
            </Typography>
            {expansion.movers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No movement yet.
              </Typography>
            ) : (
              expansion.movers.map((m) => (
                <Box
                  key={m.companyId}
                  sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderTop: "1px solid #EEE" }}
                >
                  <Typography>{m.name}</Typography>
                  <Chip
                    size="small"
                    color={m.delta >= 0 ? "success" : "warning"}
                    label={m.delta >= 0 ? `+${m.delta}` : `${m.delta}`}
                  />
                </Box>
              ))
            )}
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">Drop-off (Projects without Tasks)</Typography>
            <Divider sx={{ my: 1.25 }} />
            {dropOff.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None.
              </Typography>
            ) : (
              dropOff.map((d) => (
                <Box
                  key={d.companyId}
                  sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderTop: "1px solid #EEE" }}
                >
                  <Typography>{d.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    projects: {fmt(d.projects)}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Security & Recent Signups + License Manager */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Security & Auth</Typography>
              <SecurityIcon color="action" />
            </Box>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="body2">
              Successful logins (24h): <b>{fmt(loginPulse.last24h)}</b>
            </Typography>
            <Typography variant="body2">
              Users with a login in last 7d: <b>{fmt(loginPulse.last7d)}</b>
            </Typography>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="caption" color="text.secondary">
              Tip: for failed logins / geography, wire your auth triggers to an <i>authLogs</i> collection and chart
              here.
            </Typography>
          </Paper>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <LicenseIssuerManager onRefresh={() => setRefresher((n) => n + 1)} />
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h6">New companies (last 30d)</Typography>
            <Divider sx={{ my: 1.25 }} />
            {recentCompanies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None in the last 30 days.
              </Typography>
            ) : (
              recentCompanies.map((c) => (
                <Box
                  key={c.companyId}
                  sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderTop: "1px solid #EEE" }}
                >
                  <Typography>{c.companyName || c.companyId}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {toDate(c.createdAt)?.toLocaleDateString?.() || "-"}
                  </Typography>
                </Box>
              ))
            )}
            <Divider sx={{ my: 1.25 }} />
            <Button size="small" variant="outlined" href="/activity-log">
              Open Activity Log
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
